import { gql } from "@apollo/client";

export const USER_PARTS = gql`
  fragment UserParts on UserType {
    id
    firstName
    lastName
    username
    email
    isFirstSignIn
    biometrics {
      vitalUserId
      stepCount
      lastNightsSleep
      lastMeasuredHeartrate
    }
    userdetail {
      photo
      role {
        id
        name
      }
      studentdetail {
        id
        availableCredits
        isSubscribed
        subscriptionExipiryDate
        location {
          id
          name
        }
        company {
          id
          name
          paymentLink
        }
        plans {
          id
          name
          displayPhrase
        }
        pillar {
          name
          displayPhrase
        }
        onboarding {
          id
        }
      }
      teacherdetail {
        id
      }
    }
  }
`;

export const LEARNER_SESSION_CARD_PARTS = gql`
  fragment LearnerSessionCardParts on LearnerGroupSession {
    __typename
    id
    title
    subtitle
    pillarLabel
    topic
    duration
    filterTags
    date
    time
    timeZone
    imageUrl
    buttonState {
      isRegistered
      isSessionOngoing
      isSessionCompleted
      shouldEnableTrackBiometrics
    }
  }
`;

export const LEARNER_SESSION_CARD_WEB_PARTS = gql`
  fragment LearnerSessionCardWebParts on LearnerGroupSession {
    ...LearnerSessionCardParts
    buttonState {
      isRegistered
      isSessionOngoing
      isSessionCompleted
      shouldEnableTrackBiometrics
    }
    practitioner {
      id
      firstName
      fullName
    }
  }
  ${LEARNER_SESSION_CARD_PARTS}
`;

export const LEARNER_SESSION_CARD_BIOMETRICS_PARTS = gql`
  fragment LearnerSessionCardBiometricsParts on LearnerGroupSession {
    ...LearnerSessionCardParts
    biometrics {
      heartrateGraph {
        startValueLabel
        endValueLabel
        diffMessage
        diffValueLabel
        minValue
        maxValue
        stepValue
        values
        minTimeValue
        maxTimeValue
        stepTimeValue
        timeValues
      }
    }
  }
  ${LEARNER_SESSION_CARD_PARTS}
`;

export const LEARNER_GROUP_SESSIONS_LIST_PARTS = gql`
  fragment LearnerGroupSessionsListParts on LearnerGroupSessionsList {
    id
    label
    items {
      __typename
      ... on LearnerGroupSession {
        ...LearnerSessionCardParts
      }
    }
  }
  ${LEARNER_SESSION_CARD_PARTS}
`;

export const LEARNER_RECORDING_CARD_PARTS = gql`
  fragment LearnerRecordingCardParts on LearnerRecording {
    id
    title
    subtitle
    pillarLabel
    topic
    videoDurationText
    sessionDuration
    imageUrl
    isFavourite
    practitioner {
      fullName
    }
  }
`;

export const LEARNER_RECORDINGS_LIST_PARTS = gql`
  fragment LearnerRecordingsListParts on LearnerRecordingsList {
    id
    descriptionHtml
    label
    items {
      __typename
      ... on LearnerRecording {
        ...LearnerRecordingCardParts
      }
    }
  }
  ${LEARNER_RECORDING_CARD_PARTS}
`;

export const LEARNER_PRACTITIONER_PARTS = gql`
  fragment LearnerPractitionerParts on LearnerPractitioner {
    id
    landscapeImageUrl
    fullName
    about
    accreditation {
      title
      items
      textHtml
    }
    intro {
      title
      textHtml
      videoUrl
      videoThumbnailUrl
    }
    contentLists {
      __typename
      ... on LearnerGroupSessionsList {
        ...LearnerGroupSessionsListParts
      }
      ... on LearnerRecordingsList {
        ...LearnerRecordingsListParts
      }
    }
    isFavourite
  }
  ${LEARNER_GROUP_SESSIONS_LIST_PARTS}
  ${LEARNER_RECORDINGS_LIST_PARTS}
`;

export const LEARNER_ONBOARDING_PARTS = gql`
  fragment LearnerOnboardingParts on LearnerOnboardingForm {
    __typename
    intro {
      __typename
      titleHtml
      subtitleHtml
      buttonText
      skipButtonText
      logoutButtonText
      imageUrl
    }
    inputs {
      __typename
      ... on SingleInputScreen {
        id
        titleHtml
        subtitleHtml
        input {
          __typename
          ... on SelectInput {
            __typename
            id
            label
            required
            options {
              __typename
              label
              value
              selected
              imageUrl
              clickLink
            }
          }
          ... on MultiSelectInput {
            id
            required
            options {
              __typename
              label
              value
              selected
            }
          }
          ... on DateInput {
            id
            label
            required
          }
          ... on BooleanInput {
            id
            label
            required
            reverseLogic
            checked
          }
        }
        buttonText
        skipButtonText
      }
      ... on MultipleInputsScreen {
        id
        buttonText
        titleHtml
        subtitleHtml
        inputs {
          __typename
          ... on SelectInput {
            __typename
            id
            label
            required
            options {
              __typename
              label
              value
              selected
            }
          }
          ... on MultiSelectInput {
            id
            required
            options {
              __typename
              label
              value
            }
          }
          ... on TextInput {
            id
            label
            placeholder
            required
            minLength
            maxLength
            value
          }
          ... on DateInput {
            id
            label
            required
            value
          }
          ... on BooleanInput {
            id
            label
            required
            reverseLogic
          }
          ... on ImageInput {
            id
            label
            required
            value
          }
        }
      }
    }
    animation {
      __typename
      percentageStart
      percentageEnd
      transitionDurationSec
      textHtml
    }
  }
`;

export const LEARNER_BIOMETRICS_PARTS = gql`
  fragment LearnerBiometricsParts on LearnerBiometrics {
    vitalUserId
    providers {
      id
      name
    }
    hasHealthData
    modal {
      title
      contentHtml
      tellMeMoreButtonText
      remindMeLaterButtonText
    }
    shouldShowWebModal
    hasExperiencedTheAppleHealthConnectionPermissionsIssue
  }
`;

export const LEARNER_PARTS = gql`
  fragment LearnerParts on Learner {
    id
    firstName
    lastName
    fullName
    photoUrl
    companyName
    language
    languageIsExplicit
    oriDisclaimerAccepted
    screens {
      index {
        exercise {
          title
          subTitle
          videoUrl
          videoThumbnailUrl
          buttonText
        }
        coach {
          name
          avatarUrl
          messageTextHtml
          assessmentButton {
            text
            url
          }
        }
      }
      you {
        welcome {
          titleHtml
          subtitleHtml
        }
        indexScore {
          value
          valueFloat
          labelHtml
          pattern
          icon
        }
      }
    }
  }
`;

export const LEARNER_ARTICLE_CARD_PARTS = gql`
  fragment LearnerArticleCardParts on LearnerArticle {
    id
    title
    subtitle
    imageUrl
    pillarLabel
    readTime
    isFavourite
  }
`;

export const LEARNER_ARTICLE_PARTS = gql`
  fragment LearnerArticleParts on LearnerArticle {
    id
    title
    subtitle
    pillarLabel
    imageUrl
    contentHtml
    isFavourite
    author
  }
`;

export const LEARNER_PRACTITIONER_CARD_PARTS = gql`
  fragment LearnerPractitionerCardParts on LearnerPractitioner {
    id
    firstName
    fullName
    photoUrl
    services
  }
`;

export const LEARNER_RECORDING_PARTS = gql`
  fragment LearnerRecordingParts on LearnerRecording {
    id
    title
    subtitle
    pillarLabel
    topic
    videoUrl
    videoThumbnailUrl
    videoDurationText
    imageUrl
    about
    equipment {
      id
      label
      icon
    }
    practitioner {
      ...LearnerPractitionerCardParts
    }
    isFavourite
    scheduledCalendarEvent {
      fromDatetimeUtc
      toDatetimeUtc
    }
  }
  ${LEARNER_PRACTITIONER_CARD_PARTS}
`;

export const LEARNER_GROUP_SESSION_PARTS = gql`
  fragment LearnerGroupSessionParts on LearnerGroupSession {
    id
    title
    subtitle
    pillarLabel
    topic
    duration
    filterTags
    date
    time
    timeZone
    about
    equipment {
      id
      label
      icon
    }
    imageUrl
    practitioner {
      ...LearnerPractitionerCardParts
    }
    buttonState {
      isRegistered
      isSessionOngoing
      isSessionCompleted
      shouldEnableTrackBiometrics
    }
    biometrics {
      label
      heartrateGraph {
        startValueLabel
        endValueLabel
        diffMessage
        diffValueLabel
        minValue
        maxValue
        stepValue
        values
        minTimeValue
        maxTimeValue
        stepTimeValue
        timeValues
      }
    }
  }
  ${LEARNER_PRACTITIONER_CARD_PARTS}
`;

export const LEARNER_INDEX_SCORE_CHANGE_PARTS = gql`
  fragment LearnerIndexScoreChangeParts on LearnerIndexScoreChange {
    textHtml
    shortTextHtml
    direction
  }
`;

export const LEARNER_INDEX_SCORE_DATA_PERIOD_PARTS = gql`
  fragment LearnerIndexScoreDataPeriodParts on LearnerIndexScoreDataPeriod {
    label
    startDate
    endDate
    hasAssessmentValues
    hasBiometricsValues
    averageValue
    averageAssessmentStandaloneValue
    averageBiometricsStandaloneValue
    nextLabel
    previousLabel
    valueChange {
      ...LearnerIndexScoreChangeParts
    }
    assessmentStandaloneValueChange {
      ...LearnerIndexScoreChangeParts
    }
    biometricsStandaloneValueChange {
      ...LearnerIndexScoreChangeParts
    }
    dataPoints {
      date
      label
      value
      assessmentValue
      assessmentStandaloneValue
      biometricsValue
      biometricsStandaloneValue
    }
  }
  ${LEARNER_INDEX_SCORE_CHANGE_PARTS}
`;

export const LEARNER_INDEX_SCORE_GRAPH_PARTS = gql`
  fragment LearnerIndexScoreGraphParts on LearnerIndexScoreGraph {
    scoreTitle
    scoreSubtitle
    assessmentScoreTitle
    assessmentScoreSubtitle
    biometricsScoreTitle
    biometricsScoreSubtitle
    periods {
      week(first: $first, offset: $offset) {
        ...LearnerIndexScoreDataPeriodParts
      }
      month(first: $first, offset: $offset) {
        ...LearnerIndexScoreDataPeriodParts
      }
      year(first: $first, offset: $offset) {
        ...LearnerIndexScoreDataPeriodParts
      }
    }
  }
  ${LEARNER_INDEX_SCORE_DATA_PERIOD_PARTS}
`;

export const LEARNER_INDEX_BREAK_DOWN_PILLAR_PARTS = gql`
  fragment LearnerIndexBreakdownPillarParts on LearnerIndexBreakdownPillar {
    pillarLabel
    title
    subtitle
    score {
      pillarLabel
      value
      valueFloat
      labelHtml
      descriptionHtml
      icon
      change {
        ...LearnerIndexScoreChangeParts
      }
      graph {
        ...LearnerIndexScoreGraphParts
      }
    }
    descriptionHtml
    contentLists {
      __typename
      ... on LearnerIndexBreakdownPillarContentList {
        id
        label
        items(first: 3) {
          __typename
          ... on LearnerGroupSession {
            ...LearnerSessionCardParts
          }
          ... on LearnerRecording {
            ...LearnerRecordingCardParts
          }
          ... on LearnerArticle {
            ...LearnerArticleCardParts
          }
        }
      }
    }
    plan {
      descriptionHtml
      contentLists {
        __typename
        ... on LearnerGroupSessionsList {
          id
          label
          descriptionHtml
          items(first: 3) {
            ...LearnerSessionCardParts
          }
        }
        ... on LearnerRecordingsList {
          id
          label
          descriptionHtml
          items(first: 3) {
            ...LearnerRecordingCardParts
          }
        }
        ... on LearnerArticlesList {
          id
          label
          descriptionHtml
          items(first: 3) {
            ...LearnerArticleCardParts
          }
        }
      }
    }
  }
  ${LEARNER_INDEX_SCORE_CHANGE_PARTS}
  ${LEARNER_INDEX_SCORE_GRAPH_PARTS}
  ${LEARNER_SESSION_CARD_PARTS}
  ${LEARNER_RECORDING_CARD_PARTS}
  ${LEARNER_ARTICLE_CARD_PARTS}
`;

export const RANGE_INPUT_PARTS = gql`
  fragment RangeInputParts on RangeInput {
    __typename
    id
    label
    required
    min
    minLabel
    max
    maxLabel
    step
  }
`;

export const LEARNER_PROFILE_PARTS = gql`
  fragment LearnerProfileParts on LearnerProfileScreen {
    journey {
      title
      memberSince
      timeInvested {
        value
        label
        metric
      }
      sessionsAttended {
        value
        label
      }
    }
    goals {
      title
      hoursPerWeek {
        value
        minValue
        progressValue
        maxValue
        input {
          ... on RangeInput {
            ...RangeInputParts
          }
        }
      }
    }
    favourites {
      title
      recordings {
        count
        label
        contentList {
          __typename
          ... on LearnerRecordingsList {
            id
          }
        }
      }
      articles {
        count
        label
        contentList {
          __typename
          ... on LearnerArticlesList {
            id
          }
        }
      }
    }
    contentLists {
      __typename
      ... on LearnerGroupSessionsList {
        id
        label
        items {
          ...LearnerSessionCardParts
        }
      }
    }
  }
  ${RANGE_INPUT_PARTS}
  ${LEARNER_SESSION_CARD_PARTS}
`;

export const LEARNER_CONTENT_LIST_PARTS = gql`
  fragment LearnerContentListParts on LearnerContentListInterface {
    id
    label
    filters {
      __typename
      id
      label
      options {
        value
        label
        imageUrl
        selected
      }
    }
  }
`;

export const SELECT_INPUT_PARTS = gql`
  fragment SelectInputParts on SelectInput {
    id
    label
    required
    options {
      label
      value
      imageUrl
      selected
    }
  }
`;

export const MULTI_SELECT_INPUT_PARTS = gql`
  fragment MultiSelectInputParts on MultiSelectInput {
    id
    label
    required
    options {
      label
      value
      imageUrl
      selected
    }
  }
`;

export const BOOLEAN_INPUT_PARTS = gql`
  fragment BooleanInputParts on BooleanInput {
    id
    label
    required
    reverseLogic
    checked
  }
`;

export const DATE_INPUT_PARTS = gql`
  fragment DateInputParts on DateInput {
    id
    label
    required
    minDate
    maxDate
    value
  }
`;
export const IMAGE_INPUT_PARTS = gql`
  fragment ImageInputParts on ImageInput {
    id
    label
    required
    value
  }
`;

export const TEXT_INPUT_PARTS = gql`
  fragment TextInputParts on TextInput {
    id
    label
    required
    minLength
    maxLength
    placeholder
    value
  }
`;

export const INPUT_PARTS = gql`
  fragment InputParts on Input {
    ... on SelectInput {
      ...SelectInputParts
    }
    ... on MultiSelectInput {
      ...MultiSelectInputParts
    }
    ... on BooleanInput {
      ...BooleanInputParts
    }
    ... on DateInput {
      ...DateInputParts
    }
    ... on ImageInput {
      ...ImageInputParts
    }
    ... on TextInput {
      ...TextInputParts
    }
    ... on RangeInput {
      ...RangeInputParts
    }
  }
  ${SELECT_INPUT_PARTS}
  ${MULTI_SELECT_INPUT_PARTS}
  ${BOOLEAN_INPUT_PARTS}
  ${DATE_INPUT_PARTS}
  ${IMAGE_INPUT_PARTS}
  ${TEXT_INPUT_PARTS}
  ${RANGE_INPUT_PARTS}
`;

export const LEARNER_PROFILE_SETTINGS_PARTS = gql`
  fragment LearnerProfileSettingsParts on LearnerProfileSettings {
    title
    fields {
      photoUrl {
        ... on ImageInput {
          ...ImageInputParts
        }
      }
      firstName {
        ... on TextInput {
          ...TextInputParts
        }
      }
      lastName {
        ... on TextInput {
          ...TextInputParts
        }
      }
      injuries {
        ... on TextInput {
          ...TextInputParts
        }
      }
      birthday {
        ... on DateInput {
          ...DateInputParts
        }
      }
      gender {
        ... on SelectInput {
          ...SelectInputParts
        }
      }
    }
  }
  ${IMAGE_INPUT_PARTS}
  ${TEXT_INPUT_PARTS}
  ${DATE_INPUT_PARTS}
  ${SELECT_INPUT_PARTS}
`;

export const COMPANY_ADMIN_PARTS = gql`
  fragment CompanyAdminParts on CompanyAdmin {
    id
    firstName
    lastName
    fullName
    companyName
    photoUrl
    companyType
    companyId
    dashboardScreen {
      views
    }
  }
`;

export const LEARNER_INDEX_ASSESSMENT_PARTS = gql`
  fragment LearnerIndexAssessmentParts on LearnerIndexAssessment {
    count
    daysLeftToRetake
    insights {
      context
      encouragement
      pillar
      theme
      tips
    }
  }
`;

export const LEARNER_INDEX_SCREEN_PARTS = gql`
  fragment LearnerIndexScreenParts on LearnerIndexScreen {
    version
    showBetaFlag
    exercise {
      title
      subTitle
      videoUrl
      videoThumbnailUrl
      buttonText
    }
    score {
      pillarLabel
      value
      valueFloat
      labelHtml
      descriptionHtml
      icon
      change {
        ...LearnerIndexScoreChangeParts
      }
      graph {
        ...LearnerIndexScoreGraphParts
      }
    }
    descriptionHtml
    coach {
      name
      avatarUrl
      messageTextHtml
      assessmentButton {
        text
        url
      }
    }
    breakdown {
      energy {
        ...LearnerIndexBreakdownPillarParts
      }
      resilience {
        ...LearnerIndexBreakdownPillarParts
      }
      activity {
        ...LearnerIndexBreakdownPillarParts
      }
    }
    progress {
      title
      labels
      minValue
      maxValue
      energyData {
        label
        values
      }
      resilienceData {
        label
        values
      }
      activityData {
        label
        values
      }
    }
    biometrics {
      sessionsHistory {
        state
        callToActionTitle
        callToActionContentHtml
        callToActionButtonText
        contentList(first: 3) {
          __typename
          ... on LearnerGroupSessionsList {
            id
            label
            descriptionHtml
            items {
              ...LearnerSessionCardBiometricsParts
            }
          }
        }
      }
    }
  }
  ${LEARNER_INDEX_SCORE_CHANGE_PARTS}
  ${LEARNER_INDEX_SCORE_GRAPH_PARTS}
  ${LEARNER_INDEX_BREAK_DOWN_PILLAR_PARTS}
  ${LEARNER_SESSION_CARD_BIOMETRICS_PARTS}
`;

export const LEARNER_INDEX_BREAK_DOWN_PILLAR_V1_PARTS = gql`
  fragment LearnerIndexBreakdownPillarV1Parts on LearnerIndexBreakdownPillar {
    pillarLabel
    title
    subtitle
    score {
      value
      valueFloat
      labelHtml
      icon
    }
    descriptionHtml
    contentLists {
      __typename
      ... on LearnerIndexBreakdownPillarContentList {
        id
        label
        items(first: 3) {
          __typename
          ... on LearnerGroupSession {
            ...LearnerSessionCardParts
          }
          ... on LearnerRecording {
            ...LearnerRecordingCardParts
          }
          ... on LearnerArticle {
            ...LearnerArticleCardParts
          }
        }
      }
    }
  }
  ${LEARNER_SESSION_CARD_PARTS}
  ${LEARNER_RECORDING_CARD_PARTS}
  ${LEARNER_ARTICLE_CARD_PARTS}
`;

export const LEARNER_INDEX_SCREEN_V1_PARTS = gql`
  fragment LearnerIndexScreenV1Parts on LearnerIndexScreen {
    version
    showBetaFlag
    exercise {
      title
      subTitle
      videoUrl
      videoThumbnailUrl
      buttonText
    }
    descriptionHtml
    score {
      value
      valueFloat
      labelHtml
      descriptionHtml
      icon
    }
    coach {
      name
      avatarUrl
      messageTextHtml
      assessmentButton {
        text
        url
      }
    }
    breakdown {
      energy {
        ...LearnerIndexBreakdownPillarV1Parts
      }
      resilience {
        ...LearnerIndexBreakdownPillarV1Parts
      }
      activity {
        ...LearnerIndexBreakdownPillarV1Parts
      }
    }
    progress {
      title
      labels
      minValue
      maxValue
      energyData {
        label
        values
      }
      resilienceData {
        label
        values
      }
      activityData {
        label
        values
      }
    }
    biometrics {
      sessionsHistory {
        state
        callToActionTitle
        callToActionContentHtml
        callToActionButtonText
        contentList(first: 3) {
          __typename
          ... on LearnerGroupSessionsList {
            id
            label
            descriptionHtml
            items {
              ...LearnerSessionCardBiometricsParts
            }
          }
        }
      }
    }
  }
  ${LEARNER_INDEX_BREAK_DOWN_PILLAR_V1_PARTS}
  ${LEARNER_SESSION_CARD_BIOMETRICS_PARTS}
`;

export const LEARNER_INDEX_BREAK_DOWN_PILLAR_V2_PARTS = gql`
  fragment LearnerIndexBreakdownPillarV2Parts on LearnerIndexBreakdownPillar {
    pillarLabel
    title
    subtitle
    score {
      value
      valueFloat
      labelHtml
      descriptionHtml
      icon
      change {
        ...LearnerIndexScoreChangeParts
      }
    }
  }
  ${LEARNER_INDEX_SCORE_CHANGE_PARTS}
`;

export const LEARNER_INDEX_BREAK_DOWN_PILLAR_DETAIL_PARTS_V2 = gql`
  fragment LearnerIndexBreakdownPillarDetailPartsV2 on LearnerIndexBreakdownPillar {
    pillarLabel
    title
    subtitle
    descriptionHtml
    score {
      value
      valueFloat
      labelHtml
      descriptionHtml
      icon
      change {
        ...LearnerIndexScoreChangeParts
      }
      graph {
        ...LearnerIndexScoreGraphParts
      }
    }
    plan {
      descriptionHtml
      contentLists {
        __typename
        ... on LearnerGroupSessionsList {
          id
          label
          descriptionHtml
          items(first: 3) {
            ...LearnerSessionCardParts
          }
        }
        ... on LearnerRecordingsList {
          id
          label
          descriptionHtml
          items(first: 3) {
            ...LearnerRecordingCardParts
          }
        }
        ... on LearnerArticlesList {
          id
          label
          descriptionHtml
          items(first: 3) {
            ...LearnerArticleCardParts
          }
        }
      }
    }
  }
  ${LEARNER_INDEX_SCORE_GRAPH_PARTS}
  ${LEARNER_SESSION_CARD_PARTS}
  ${LEARNER_RECORDING_CARD_PARTS}
  ${LEARNER_ARTICLE_CARD_PARTS}
`;

export const LEARNER_INDEX_CTA_PARTS = gql`
  fragment LearnerIndexCTAParts on LearnerIndexCTAs {
    actions {
      takeAssessment {
        buttonText
        assessmentUrl
      }
      connectBiometrics {
        buttonText
      }
    }
    prompts {
      id
      imageUrl
      descriptionHtml
      variant
      action {
        __typename
        ... on LearnerIndexCTAActionConnectBiometrics {
          __typename
          buttonText
        }
        ... on LearnerIndexCTAActionTakeAssessment {
          __typename
          buttonText
          assessmentUrl
        }
      }
    }
  }
`;
export const LEARNER_INDEX_SCREEN_V2_PARTS = gql`
  fragment LearnerIndexScreenV2Parts on LearnerIndexScreen {
    version
    showBetaFlag
    exercise {
      title
      subTitle
      videoUrl
      videoThumbnailUrl
      buttonText
    }
    descriptionHtml
    score {
      value
      valueFloat
      labelHtml
      descriptionHtml
      icon
      change {
        ...LearnerIndexScoreChangeParts
      }
      graph {
        ...LearnerIndexScoreGraphParts
      }
    }
    coach {
      name
      avatarUrl
      messageTextHtml
      assessmentButton {
        text
        url
      }
    }
    breakdown {
      energy {
        ...LearnerIndexBreakdownPillarV2Parts
      }
      resilience {
        ...LearnerIndexBreakdownPillarV2Parts
      }
      activity {
        ...LearnerIndexBreakdownPillarV2Parts
      }
    }
    biometrics {
      sessionsHistory {
        state
        callToActionTitle
        callToActionContentHtml
        callToActionButtonText
        contentList(first: 3) {
          __typename
          ... on LearnerGroupSessionsList {
            id
            label
            descriptionHtml
            items {
              ...LearnerSessionCardBiometricsParts
            }
          }
        }
      }
    }
    CTAs {
      ...LearnerIndexCTAParts
    }
  }
  ${LEARNER_INDEX_SCORE_CHANGE_PARTS}
  ${LEARNER_INDEX_SCORE_GRAPH_PARTS}
  ${LEARNER_INDEX_BREAK_DOWN_PILLAR_V2_PARTS}
  ${LEARNER_SESSION_CARD_BIOMETRICS_PARTS}
  ${LEARNER_INDEX_CTA_PARTS}
`;

export const LEARNER_MIXED_CONTENT_LIST_PARTS = gql`
  fragment LearnerMixedContentListParts on LearnerMixedContentList {
    id
    label
    descriptionHtml
    items {
      ... on LearnerGroupSession {
        ...LearnerSessionCardParts
      }
      ... on LearnerRecording {
        ...LearnerRecordingCardParts
      }
      ... on LearnerArticle {
        ...LearnerArticleCardParts
      }
    }
  }

  ${LEARNER_SESSION_CARD_PARTS}
  ${LEARNER_RECORDING_CARD_PARTS}
  ${LEARNER_ARTICLE_CARD_PARTS}
`;

export const OVERALL_INDEX_SCORE_PARTS = gql`
  fragment OverallIndexScoreParts on OverallIndexScore {
    __typename
    id
    shorthand
    name
    value
    minValue
    maxValue
    label
    labelKey
    blurb
    scoreDifference
  }
`;

export const CATEGORY_SCORE_PARTS = gql`
  fragment CategoryScoreParts on CategoryScore {
    __typename
    id
    shorthand
    name
    value
    minValue
    maxValue
    label
    labelKey
    blurb
  }
`;

export const DOMAIN_SCORE_PARTS = gql`
  fragment DomainScoreParts on DomainScore {
    __typename
    id
    shorthand
    name
    value
    minValue
    maxValue
    label
    labelKey
    blurb
    scoreDifference
    categoryShorthand
    recommendedRecordings {
      subTitle
      recording {
        ... on LearnerRecording {
          ...LearnerRecordingParts
        }
      }
    }
  }

  ${LEARNER_RECORDING_PARTS}
`;

export const THEME_SCORE_PARTS = gql`
  fragment ThemeScoreParts on ThemeScore {
    __typename
    id
    shorthand
    name
    value
    minValue
    maxValue
    label
    labelKey
    blurb
    domainShorthand
  }
`;

export const ITEM_SCORE_PARTS = gql`
  fragment ItemScoreParts on ItemScore {
    __typename
    id
    shorthand
    name
    value
    minValue
    maxValue
    label
    labelKey
    blurb
    themeShorthand
  }
`;

// minimal fields (for lists/trends)
export const INDEX_SCORE_SNAPSHOT_MINIMAL_PARTS = gql`
  fragment IndexScoreSnapshotMinimalParts on IndexScoreSnapshot {
    id
    takenAt
    responseId
    summary
    overallIndex {
      ...OverallIndexScoreParts
    }
  }

  ${OVERALL_INDEX_SCORE_PARTS}
`;

// complete fields (for detailed view)
export const INDEX_SCORE_SNAPSHOT_PARTS = gql`
  fragment IndexScoreSnapshotParts on IndexScoreSnapshot {
    id
    takenAt
    responseId
    overallIndex {
      ...OverallIndexScoreParts
    }
    domains {
      ...DomainScoreParts
    }
    themes {
      ...ThemeScoreParts
    }
    items {
      ...ItemScoreParts
    }
  }

  ${OVERALL_INDEX_SCORE_PARTS}
  ${DOMAIN_SCORE_PARTS}
  ${THEME_SCORE_PARTS}
  ${ITEM_SCORE_PARTS}
`;

// Use this when you only need the overall index value
export const LEARNER_INDEX_SCORES_MINIMAL_PARTS = gql`
  fragment LearnerIndexScoresMinimalParts on LearnerIndexScores {
    overallIndex {
      ...OverallIndexScoreParts
    }
    takenAt
    responseId
    summary
  }
  ${OVERALL_INDEX_SCORE_PARTS}
`;

// Use this for dashboard/overview screens showing current state
export const LEARNER_INDEX_SCORES_CURRENT_PARTS = gql`
  fragment LearnerIndexScoresCurrentParts on LearnerIndexScores {
    __typename
    overallIndex {
      ...OverallIndexScoreParts
    }
    takenAt
    responseId
    summary
    domains {
      ...DomainScoreParts
    }
    themes {
      ...ThemeScoreParts
    }
    items {
      ...ItemScoreParts
    }
  }
  ${OVERALL_INDEX_SCORE_PARTS}
  ${DOMAIN_SCORE_PARTS}
  ${THEME_SCORE_PARTS}
  ${ITEM_SCORE_PARTS}
`;

export const ASSESSMENT_SCHEDULE_PARTS = gql`
  fragment AssessmentScheduleParts on AssessmentSchedule {
    __typename
    previousAssessmentResponseId
    previousAssessmentTakenAt
    nextAssessmentDueAt
    isNextAssessmentDue
    daysUntilNextAssessmentDue
    hasResumableProgress
    resumableProgressExpiresAt
    resumableProgressSavedAt
  }
`;
